// client/src/components/common/ErrorState.jsx
//
// Shared "it failed" state.
//
// Replaces a spread of ad hoc treatments — Dashboard rendered errors as
// <p style={{ color: 'red' }}>, which is a contrast bug waiting to happen
// (red on whatever the theme's page background is) as well as being
// untranslated; PostsList used a raw Bootstrap .alert-danger.
//
// Uses the .note.error surface from styles/surfaces.css, so background and
// foreground are declared together and the pair stays legible in every theme.
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @param {object}   props
 * @param {string}   [props.message]  Pre-resolved message (e.g. from an API).
 * @param {string}   [props.messageKey='errors.generic']  i18n key, used when
 *   no `message` is given.
 * @param {Function} [props.onRetry]  When supplied, renders a retry button.
 * @param {string}   [props.retryLabelKey='common.tryAgain']
 * @param {string}   [props.className='']
 */
export default function ErrorState({
  message,
  messageKey = 'errors.generic',
  onRetry,
  retryLabelKey = 'common.tryAgain',
  className = '',
}) {
  const { t } = useTranslation();
  const text = message || t(messageKey);

  return (
    <div
      className={`note error note-block error-state ${className}`.trim()}
      // assertive: the user's action failed and they need to know now.
      role="alert"
      aria-live="assertive"
      data-testid="error-state"
    >
      <div className="error-state__message">{text}</div>
      {onRetry && (
        <button
          type="button"
          className="error-state__retry"
          onClick={onRetry}
        >
          {t(retryLabelKey)}
        </button>
      )}
    </div>
  );
}
